import { Freelancer } from "../models/Freelancer";
import JSONPatchOperation from "../utils/JSONPatchOperation";
import apiClient from "./api-client";
import { handleApiError } from "./error-handler";

/**
 * Get a specific freelancer
 * @param id Freelancer ID
 */
export const getFreelancer = async (
	id: number,
	freelancerKey: string = ""
): Promise<Freelancer | null> => {
	const response = await apiClient.get(`/freelancers/${id}`, {
		headers: {
			"Freelancer-Key": freelancerKey
		}
	});

	return response.data;
};

/**
 * Create a new freelancer
 */
export const createFreelancer = async (freelancer: Freelancer): Promise<Freelancer> => {
	const response = await apiClient.post(`/freelancers`, freelancer);
	return response.data;
};

/**
 * Search freelancers by email
 */
export const searchFreelancers = async (email: string): Promise<Freelancer[]> => {
	const response = await apiClient.post(`/freelancers/search`, { email });
	return response.data;
};

/**
 * Update a freelancer (PUT)
 * @param id Freelancer ID
 * @param freelancer Updated freelancer data
 */
export const updateFreelancer = async (
	id: number,
	freelancer: Freelancer
): Promise<Freelancer> => {
	const response = await apiClient.put(`/freelancers/${id}`, freelancer);
	return response.data;
};

/**
 * Update a freelancer using JSON Patch (PATCH)
 * @param id Freelancer ID
 * @param patchOperations Array of JSON Patch operations
 */
export const patchFreelancer = async (
	id: number,
	patchOperations: JSONPatchOperation[]
): Promise<Freelancer> => {
	try {
		const response = await apiClient.patch(`/freelancers/${id}`, patchOperations, {
			headers: {
				"Content-Type": "application/json-patch+json"
			}
		});
		return response.data;
	} catch (error) {
		throw handleApiError(error, "Failed to update freelancer");
	}
};

/**
 * Get current user's freelancer profile
 */
export const getCurrentFreelancer = async (): Promise<Freelancer | null> => {
	try {
		const response = await apiClient.get(`/freelancers/me`);

		return response.data;
	} catch (error: any) {
		if (error.response?.status === 404) {
			return null;
		}
		throw new Error(error.response?.data?.message ?? "Not able to get the current user.");
	}
};
